import { useState, useMemo } from 'react';
import { FilterSection } from '../../components/Digital/FilterSection';
import { AssetCard } from '../../components/Digital/AssetCard';
import { useGetPublicDigitalAssets } from '../../functions/digitalAssetHook';

interface DigitalAsset {
  _id: string;
  id: string;
  title: string;
  description: string;
  category: string;
  imageUrl: string;
  previewUrl: string;
  price: number;
  tags: string[];
}

// Componente Skeleton Loading para simular carga
const SkeletonLoader = () => (
  <div className="animate-pulse">
    <div className="mb-12 space-y-4">
      <div className="h-12 bg-gray-200 rounded-lg w-full max-w-xl mx-auto" />
      <div className="flex flex-wrap gap-2 justify-center">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-8 bg-gray-200 rounded-full w-24" />
        ))}
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="h-48 bg-gray-200" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-full" />
            <div className="h-3 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AssetList: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { publicDigitalAssets, isLoading, isError, error } = useGetPublicDigitalAssets();

  // Extraer y filtrar categorías únicas de los activos
  const categories: string[] = useMemo(() => {
    if (!publicDigitalAssets?.assets) return [];
    return Array.from(
      new Set(publicDigitalAssets.assets.map((asset: DigitalAsset) => asset.category))
    );
  }, [publicDigitalAssets]);

  // Filtrar activos por categoría, título, descripción y tags
  const filteredAssets = useMemo(() => {
    if (!publicDigitalAssets?.assets) return [];

    return publicDigitalAssets.assets.filter((asset: DigitalAsset) => {
      const lowerSearchTerm = searchTerm.toLowerCase();

      // Verificar si coincide con la categoría seleccionada
      const matchesCategory = !selectedCategory || asset.category === selectedCategory;

      // Verificar si coincide con el término de búsqueda en título, descripción, categoría o tags
      const matchesSearch =
        !searchTerm ||
        asset.title.toLowerCase().includes(lowerSearchTerm) ||
        asset.description.toLowerCase().includes(lowerSearchTerm) ||
        asset.category.toLowerCase().includes(lowerSearchTerm) ||
        asset.tags.some((tag) => tag.toLowerCase().includes(lowerSearchTerm)); // Buscar en los tags

      return matchesCategory && matchesSearch;
    });
  }, [publicDigitalAssets, selectedCategory, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-6 lg:px-8 xl:px-24">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-navy-900 mb-3">Digital Assets</h1>
          <p className="text-lg text-navy-700 max-w-3xl mx-auto">
            Discover and explore our collection of high-quality digital assets for your next project.
          </p>
        </div>

        {/* Error Handling */}
        {isError && <p className="text-center text-red-500">Error: {error?.message}</p>}

        {/* Skeleton Loader while loading */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          publicDigitalAssets && (
            <>
              {/* Filtro de Categoría y Búsqueda */}
              <div className="mb-12">
                <FilterSection
                  categories={categories}
                  selectedCategory={selectedCategory}
                  searchTerm={searchTerm}
                  onCategoryChange={setSelectedCategory}
                  onSearchChange={setSearchTerm}
                />
              </div>

              {/* Grid de Activos Digitales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-80">
                {filteredAssets.map((asset: DigitalAsset) => (
                  <AssetCard key={asset._id} asset={asset} />
                ))}
              </div>

              {/* Mensaje cuando no hay resultados */}
              {filteredAssets.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-xl">No assets found matching your criteria</p>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};